import { useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { Icon, cn, type IconName } from '@forecastify/ui';
import { useI18n, useT } from '../i18n';
import { formatNumber } from '../chrome/format';
import { failedJobCount, problemCount, type Health, type HealthState } from '../use-health';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ *ÁLLAPOT MOST* — four tiles, coloured by what they need from you.         │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️⚠️ **THE MOCKUP'S TILES, CLASS FOR CLASS.** A tile with nothing in it is a plain card with a
 * muted zero; one with something in it takes the tone of what it is — red for broken work, amber
 * for the agent ceiling — so the strip reads at a glance before a single number does. The first
 * version drew every tile the same and put the count in the corner; the redesign's point is that
 * the colour IS the summary.
 *
 * ⚠️ **The headline counts tasks, not tiles** — *6 tétel vár rád* is 1 + 3 + 2. A suspended
 * organisation is a decision already made, so it has a tile and no say in the headline.
 *
 * ⚠️ **The time is when the strip was READ**, not the time now. *"Nothing needs attention · 09:52"*
 * is a claim about 09:52, and an operator who left the tab open over lunch deserves to know that.
 */

type Severity = 'bad' | 'warn' | 'neutral';

interface Tile {
  readonly key: string;
  readonly label: string;
  readonly icon: IconName;
  readonly count: number;
  readonly sub: string;
  readonly severity: Severity;
  readonly go: string;
}

/* The mockup's three class sets, keyed on (has something, severity). Tokens per S-17. */
const SURFACE: Readonly<Record<Severity, string>> = {
  bad: 'border-destructive/40 bg-destructive/[0.05] hover:bg-destructive/[0.09]',
  warn: 'border-[color:var(--status-warn-line)] bg-[var(--status-warn-soft)] hover:brightness-[.98]',
  neutral: 'bg-card hover:bg-muted/40',
};
const NUMBER: Readonly<Record<Severity, string>> = {
  bad: 'text-destructive',
  warn: 'text-[var(--status-warn-text)]',
  neutral: '',
};
const LABEL: Readonly<Record<Severity, string>> = {
  bad: 'text-destructive font-medium',
  warn: 'text-[var(--status-warn-text)] font-medium',
  neutral: 'text-muted-foreground',
};

export function HealthStrip({ state }: { readonly state: HealthState }): ReactNode {
  const t = useT();
  const { locale } = useI18n();
  const navigate = useNavigate();
  /* Read once, when the strip first had an answer — see the header. */
  const [readAt] = useState(() => {
    const now = new Date();
    return `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
  });

  if (state.kind === 'loading') return null;

  if (state.kind === 'failed') {
    return (
      <section className="flex flex-col gap-2" data-health="failed">
        <h2 className="text-[11px] font-medium tracking-[.03em] uppercase">
          {t('health.heading')}
        </h2>
        {/* ⚠️ It says the STRIP failed, not that nothing is wrong. Those are opposite claims, and
            an empty strip would make the more reassuring one. */}
        <div className="text-muted-foreground flex items-center gap-2 rounded-lg border border-dashed px-3 py-2.5 text-[12px]">
          <Icon name="triangle-alert" size={14} />
          <span>{t('health.failed')}</span>
        </div>
      </section>
    );
  }

  const health = state.health;
  const problems = problemCount(health);
  const tiles = tilesOf(health, t);

  return (
    <section className="flex flex-col gap-2" aria-label={t('health.heading')}>
      <div className="flex items-baseline gap-2">
        <h2 className="text-[11px] font-medium tracking-[.03em] uppercase">
          {t('health.heading')}
        </h2>
        <span className="text-muted-foreground text-[12px]">
          {problems > 0
            ? `${t('health.count', { count: problems })} · ${readAt}`
            : `${t('health.quiet')} · ${readAt}`}
        </span>
      </div>
      <div className="grid grid-cols-[repeat(auto-fit,minmax(210px,1fr))] gap-2">
        {tiles.map((tile) => {
          const live = tile.count > 0;
          return (
            <button
              key={tile.key}
              type="button"
              data-tile={tile.key}
              onClick={() => {
                void navigate(tile.go);
              }}
              className={cn(
                'group flex min-w-0 flex-col gap-1.5 rounded-lg border p-3 text-left transition-colors',
                live ? SURFACE[tile.severity] : SURFACE.neutral,
              )}
            >
              <span
                className={cn(
                  'flex items-center gap-1.5 text-[12px]',
                  live ? LABEL[tile.severity] : 'text-muted-foreground',
                )}
              >
                <Icon name={tile.icon} size={13} className="shrink-0" />
                <span className="min-w-0 flex-1 truncate">{tile.label}</span>
                <Icon
                  name="arrow-right"
                  size={12}
                  className="shrink-0 opacity-0 transition-opacity group-hover:opacity-100"
                />
              </span>
              <span
                className={cn(
                  'text-[24px] leading-[1.1] font-semibold tracking-[-.02em] tabular-nums',
                  live ? NUMBER[tile.severity] : 'text-muted-foreground',
                )}
              >
                {formatNumber(tile.count, locale)}
              </span>
              <span className="text-muted-foreground truncate text-[11.5px]">
                {live ? tile.sub : t('health.none')}
              </span>
            </button>
          );
        })}
      </div>
    </section>
  );
}

function tilesOf(health: Health, t: ReturnType<typeof useT>): readonly Tile[] {
  return [
    {
      key: 'overdue',
      label: t('health.overdue'),
      icon: 'clock',
      count: health.overdue.length,
      sub: health.overdue
        .map((item) =>
          item.missed === undefined
            ? item.label
            : `${item.label} · ${t('health.missed', { count: item.missed })}`,
        )
        .join(', '),
      severity: 'bad',
      go: '/operations',
    },
    {
      key: 'failed',
      label: t('health.failedJobs'),
      icon: 'triangle-alert',
      count: failedJobCount(health),
      /* ⚠️ The KINDS, once each — the number above already says how many jobs. */
      sub: health.failedJobs.map((item) => item.label).join(', '),
      severity: 'bad',
      go: '/operations',
    },
    {
      key: 'ceiling',
      label: t('health.atCeiling'),
      icon: 'sparkles',
      count: health.atCeiling.length,
      sub: health.atCeiling.map((item) => item.label).join(', '),
      severity: 'warn',
      go: '/agent-spend',
    },
    {
      key: 'suspended',
      label: t('health.suspended'),
      icon: 'circle-dot',
      count: health.suspended.length,
      sub: health.suspended.map((item) => item.label).join(', '),
      severity: 'neutral',
      go: '/tenants?status=suspended',
    },
  ];
}
